import { Dialog } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { HiCheck } from 'react-icons/hi';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@src/components';
import Modal from '@src/components/Modal';
import {
  OrderVoucherType,
  useCheckFiscalRepPaymentStatusMutation,
  useCheckOrderVoucherPaymentStatusMutation,
} from '@src/generated/graphql';
import useEffectOnce from '@src/hooks/useEffectOnce';
import { useToast } from '@src/hooks/useToast';
import { VOUCHER_CONFIGS } from '@src/pages/orders/constants/vouchers';
import { logError } from '@src/services/error-logger';

function PaymentSuccessModal(): JSX.Element {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const type = searchParams.get('type') as
    | 'voucher'
    | 'fiscal_rep_subscription';
  const voucherType = searchParams.get('voucher_type') as OrderVoucherType;
  const voucherConfig = VOUCHER_CONFIGS[voucherType || ''];
  const checkoutSessionId = searchParams.get('session_id');
  const showPaymentSuccessModal =
    ((voucherConfig || type === 'fiscal_rep_subscription') &&
      searchParams.has('show_payment_success')) ||
    false;

  const [checkOrderVoucherPaymentStatus] =
    useCheckOrderVoucherPaymentStatusMutation();

  const [checkFiscalRepPaymentStatus] =
    useCheckFiscalRepPaymentStatusMutation();

  useEffectOnce(() => {
    if (
      searchParams.has('show_payment_success') &&
      type === 'voucher' &&
      !voucherConfig
    ) {
      logError(new Error(`Invalid voucher type passed in: ${voucherType}`));
    }
  });

  useEffectOnce(() => {
    if (checkoutSessionId && showPaymentSuccessModal) {
      if (type === 'voucher') {
        checkOrderVoucherPaymentStatus({
          variables: {
            input: { checkoutSessionId },
          },
        }).catch((err) => {
          logError(err);
          toast.error(
            t(
              'dashboard.error_payment_refresh',
              'Sorry, we had an error refreshing your order. Please refresh the page and try again.',
            ),
          );
        });
      }
      if (type === 'fiscal_rep_subscription') {
        checkFiscalRepPaymentStatus({
          variables: {
            input: { checkoutSessionId },
          },
        }).catch((err) => {
          logError(err);
          toast.error(
            t(
              'dashboard.error_payment_subscription_refresh',
              'Sorry, we had an error refreshing your subscription data. Please refresh the page and try again.',
            ),
          );
        });
      }
    }
  });

  function closePaymentModal(): void {
    setSearchParams({});
  }

  return (
    <Modal isOpen={showPaymentSuccessModal} onClose={() => closePaymentModal()}>
      <div>
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <HiCheck className="h-6 w-6 text-green-600" aria-hidden="true" />
        </div>
        <div className="mt-3 text-center sm:mt-5">
          <Dialog.Title
            as="h3"
            className="text-lg font-medium leading-6 text-gray-900"
          >
            {type === 'voucher'
              ? t('dashboard.payment_success.title', 'Payment successful!')
              : t(
                  'dashboard.payment_success.subscription_updated',
                  'Subscription updated!',
                )}
          </Dialog.Title>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              {type === 'voucher'
                ? t(
                    'dashboard.payment_success.description_voucher',
                    'Thanks for placing an order with Bordr. Please complete the intake form so we can start processing your order.',
                  )
                : t(
                    'dashboard.payment_success.description_subscription',
                    'Your payment method has been successfully updated.',
                  )}
            </p>
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-6">
        {type === 'voucher' ? (
          <Button
            className="w-full justify-center"
            onClick={() => navigate(voucherConfig.intakeUrl)}
          >
            {t('dashboard.payment_success.start_intake_button', 'Start intake')}
          </Button>
        ) : (
          <Button
            className="w-full justify-center"
            onClick={() => closePaymentModal()}
          >
            {t('dashboard.payment_success.see_orders_button', 'See my orders')}
          </Button>
        )}
      </div>
    </Modal>
  );
}

export default PaymentSuccessModal;
